import type { Role } from '@codot/contracts';
import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '../stores/auth.store';

declare module 'vue-router' {
  interface RouteMeta {
    /** Reachable without a session (login, signup, invitation acceptance). */
    public?: boolean;
    /** Signed-in users are bounced away (login/signup). */
    guestOnly?: boolean;
    /** Minimum role in the active organization. UX only — the API enforces the real rule. */
    minRole?: Role;
  }
}

export const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: () => import('../views/LoginView.vue'),
      meta: { public: true, guestOnly: true },
    },
    {
      path: '/signup',
      name: 'signup',
      component: () => import('../views/SignupView.vue'),
      meta: { public: true, guestOnly: true },
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('../views/ForgotPasswordView.vue'),
      meta: { public: true, guestOnly: true },
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('../views/ResetPasswordView.vue'),
      meta: { public: true },
      props: (route) => ({ token: String(route.query.token ?? '') }),
    },
    {
      path: '/accept-invite',
      name: 'accept-invite',
      component: () => import('../views/AcceptInviteView.vue'),
      meta: { public: true },
      props: (route) => ({ token: String(route.query.token ?? '') }),
    },
    // Phase 16 (legacy CODOT / OnSign IA): Content is home; Players, Player Groups and Campaigns follow the
    // legacy vocabulary. Route NAMES are kept (guards, redirects and links target them); old paths redirect.
    { path: '/', name: 'dashboard', component: () => import('../views/ContentView.vue') },
    // Phase 16 Step 6: a folder is a real, deep-linkable route (legacy /dashboard/folders/{id}).
    { path: '/folders/:folderId', name: 'folder', component: () => import('../views/ContentView.vue') },
    { path: '/media', name: 'media', redirect: '/' },
    { path: '/monitoring', name: 'monitoring', component: () => import('../views/MonitoringView.vue') },
    { path: '/apps', name: 'apps', component: () => import('../views/AppsView.vue') },
    { path: '/apps/:appId', name: 'custom-app', component: () => import('../views/CustomAppView.vue'), props: true },
    { path: '/data-feeds', name: 'data-feeds', component: () => import('../views/DataFeedsView.vue') },
    { path: '/data-feeds/:feedId', name: 'data-feed', component: () => import('../views/DataFeedView.vue'), props: true },
    { path: '/players', name: 'players', component: () => import('../views/PlayersView.vue') },
    { path: '/players/setup/chromeos', name: 'chromeos-setup', component: () => import('../views/ChromeOsSetupView.vue') },
    { path: '/screens/:rest(.*)*', redirect: (to) => to.fullPath.replace(/^\/screens/, '/players') },
    {
      // Phase 7: player page = header + sub-navigation (legacy CODOT sections); each tab is a child route,
      // so tabs are linkable and the browser back button works.
      path: '/players/:screenId',
      component: () => import('../views/screen/ScreenLayout.vue'),
      props: true,
      children: [
        {
          path: '',
          name: 'screen-detail',
          component: () => import('../views/screen/PlayerStatusTab.vue'),
          props: true,
        },
        {
          path: 'content-in-player',
          name: 'screen-published',
          component: () => import('../views/screen/ContentInPlayerTab.vue'),
          props: true,
        },
        {
          path: 'activity',
          name: 'screen-activity',
          component: () => import('../views/screen/PlayerActivityTab.vue'),
          props: true,
        },
        {
          path: 'resources',
          name: 'screen-resources',
          component: () => import('../views/screen/ResourceUsageTab.vue'),
          props: true,
        },
        {
          path: 'logs',
          name: 'screen-logs',
          component: () => import('../views/screen/ErrorsWarningsTab.vue'),
          props: true,
        },
        {
          path: 'live',
          name: 'screen-live',
          component: () => import('../views/screen/LiveControlTab.vue'),
          props: true,
        },
        {
          // Phase 16 Step 11: touch / remote-key interactions (legacy "Interactivity" tab).
          path: 'interactivity',
          name: 'screen-interactivity',
          component: () => import('../views/screen/PlayerInteractivityTab.vue'),
          props: true,
        },
        {
          // Phase 16 Step 8: legacy "Player Settings" (sections that take effect on the device).
          path: 'settings',
          name: 'screen-settings',
          component: () => import('../views/screen/PlayerSettingsTab.vue'),
          props: true,
        },
        {
          // The compiled manifest, for support (was "Content & Settings"; its settings moved to Player Settings).
          path: 'content',
          name: 'screen-content',
          component: () => import('../views/screen/ContentSettingsTab.vue'),
          props: true,
        },
      ],
    },
    { path: '/player-groups', name: 'groups', component: () => import('../views/PlayerGroupsView.vue') },
    { path: '/groups', redirect: '/player-groups' },
    // Phase 16 Step 12: legacy top-level Analytics and Region pages.
    { path: '/analytics', name: 'analytics', component: () => import('../views/AnalyticsView.vue') },
    { path: '/regions', name: 'regions', component: () => import('../views/RegionsView.vue') },
    { path: '/tags', name: 'tags', component: () => import('../views/TagsView.vue') },
    { path: '/billing/return', name: 'billing-return', component: () => import('../views/BillingReturnView.vue') },
    { path: '/billing/test-pay', name: 'billing-test-pay', component: () => import('../views/BillingTestPayView.vue') },
    // Phase 16 Step 13: Health Check and Activity Log (onsign §14 target).
    { path: '/health-check', name: 'health-check', component: () => import('../views/HealthCheckView.vue') },
    { path: '/alerts', name: 'alerts', component: () => import('../views/AlertsView.vue') },
    { path: '/alerts/new', name: 'alert-new', component: () => import('../views/AlertEditorView.vue') },
    { path: '/alerts/:alertId', name: 'alert-edit', component: () => import('../views/AlertEditorView.vue'), props: true },
    { path: '/activity-log', name: 'activity-log', component: () => import('../views/ActivityLogView.vue') },
    // Phase 16 Step 14: Reports (Report Hub pattern; ?new=<type> opens the wizard).
    { path: '/reports', name: 'reports', component: () => import('../views/ReportsView.vue') },
    // Campaigns live in Content (legacy); the editor is a full page (a timeline needs the room).
    { path: '/campaigns', name: 'layouts', redirect: '/' },
    { path: '/layouts', redirect: '/' },
    { path: '/campaigns/new', name: 'campaign-new', component: () => import('../views/CampaignEditorView.vue') },
    {
      path: '/campaigns/:campaignId',
      name: 'campaign',
      component: () => import('../views/CampaignEditorView.vue'),
      props: true,
    },
    // Compositions (free-form designs): a full-page editor; ?template= starts from a template, ?folder= files it.
    { path: '/compositions/new', name: 'composition-new', component: () => import('../views/CompositionEditorView.vue') },
    { path: '/compositions/:assetId', name: 'composition', component: () => import('../views/CompositionEditorView.vue'), props: true },
    { path: '/playlists/new', name: 'playlist-new', component: () => import('../views/PlaylistEditorView.vue') },
    {
      path: '/playlists/:playlistId',
      name: 'playlist',
      component: () => import('../views/PlaylistEditorView.vue'),
      props: true,
    },
    {
      path: '/settings/organization',
      name: 'organization-settings',
      component: () => import('../views/OrganizationSettingsView.vue'),
      meta: { minRole: 'VIEWER' },
    },
    { path: '/settings/attributes', name: 'custom-attributes', component: () => import('../views/CustomAttributesView.vue'), meta: { minRole: 'VIEWER' } },
    { path: '/:pathMatch(.*)*', redirect: '/' },
  ],
});

/** Navigation guard: protect everything that isn't explicitly public. */
router.beforeEach(async (to) => {
  const auth = useAuthStore();
  await auth.ensureLoaded();

  if (!to.meta.public && !auth.isAuthenticated) {
    return { name: 'login', query: to.fullPath !== '/' ? { redirect: to.fullPath } : {} };
  }
  if (to.meta.guestOnly && auth.isAuthenticated) return { name: 'dashboard' };
  if (to.meta.minRole && !auth.can(to.meta.minRole)) return { name: 'dashboard' };
  return true;
});
